/**
 * Alfabety, klasy części mowy i pisownia dla Wordle'a.
 *
 * **Gra nigdy nie składa pisowni.** Litery porównuje się dosłownie: `ł` to nie
 * `l`, `ą` to nie `a`. Ustawienie partii `ogonki` przełącza nie sposób
 * porównywania, tylko **zbiór słów**:
 *
 *  - `ogonki: false` (domyślnie) — do gry wchodzą wyłącznie słowa, które
 *    ogonków **nie mają w ogóle**: `tylko`, `teraz`, `kiedy`. `jeśli` nie
 *    wchodzi, bo ma `ś`. Klawiatura ma 23 (pl) / 26 (en) / 27 (es) klawiszy.
 *  - `ogonki: true` — wchodzą **wszystkie** słowa, w pełnej pisowni, a
 *    klawiatura dostaje klawisz „dodaj ogonek".
 *
 * Poprzednia wersja składała pisownię (`ŻÓŁTY` → `zolty`) i to jest zmiana,
 * która ją cofa. Składanie było ukrytą równością `ł = l`: pozwalało wpisać
 * `jesli`, czyli ciąg, który nie jest polskim słowem, i kazało dwunastolatkowi
 * zgadywać hasło w pisowni, której nigdzie nie widział. `zloz` niżej zostaje,
 * ale **wyłącznie dla potoku danych** — patrz jego komentarz.
 *
 * Plik stoi w `shared`, bo **klient też go potrzebuje**: klawiatura rysuje
 * alfabet i cykl wariantów z ogonkiem, a pole wpisywania normalizuje ciąg przed
 * wysłaniem. Skrypty budujące dane (`dict:wordle`, `dict:tagi`, `dict:hasla`)
 * importują go przez cienki reeksport w `server/src/dictionary/wordle/litery.ts`.
 */
export const JEZYKI = ["pl", "en", "es"];
export function jestJezykiem(x) {
    return JEZYKI.includes(x);
}
/** Nazwa języka do interfejsu. Jedno miejsce, żeby nie rozjechały się ekrany. */
export const NAZWY_JEZYKOW = {
    pl: "polski",
    en: "angielski",
    es: "hiszpański",
};
/**
 * Alfabet **bez ogonków** — partia z `ogonki: false`.
 *
 * `pl` nie ma `q v x` (polski alfabet ich nie ma), `es` ma `ñ` jako osobną
 * literę między `n` a `o`: `ñ` **nie jest** literą z akcentem, tylko osobną
 * literą hiszpańskiego alfabetu, więc zostaje także w trybie bez ogonków.
 * `caña` jest w tym trybie legalne, `cañó` nie.
 */
export const ALFABETY = {
    pl: "abcdefghijklmnoprstuwyz",
    en: "abcdefghijklmnopqrstuvwxyz",
    es: "abcdefghijklmnñopqrstuvwxyz",
};
/**
 * Alfabet **pełny** — partia z `ogonki: true`.
 *
 * Kolejność alfabetyczna języka, nie „litery bazowe, potem ogonki": `ą` stoi za
 * `a`, `ł` za `l`. Klawiatura rysuje ten ciąg wprost, a szukanie `ł` na końcu
 * czwartego rzędu byłoby wolniejsze niż szukanie go tuż za `l`.
 *
 *  - `pl` — 32 litery (23 + `ą ć ę ł ń ó ś ź ż`),
 *  - `en` — 26, identyczne z `ALFABETY.en`: angielski ogonków nie ma, więc
 *    ustawienie nic w nim nie zmienia. Wpis stoi tu mimo to, żeby `alfabetGry`
 *    nie potrzebowało gałęzi „a jeśli angielski";
 *  - `es` — 33 (27 + `á é í ó ú ü`).
 */
export const ALFABETY_OGONKI = {
    pl: "aąbcćdeęfghijklłmnńoóprsśtuwyzźż",
    en: "abcdefghijklmnopqrstuvwxyz",
    es: "aábcdeéfghiíjklmnñoópqrstuúüvwxyz",
};
/**
 * Pisownia partii **zapisanej przed** tym ustawieniem: bez ogonków.
 *
 * To nie jest wybór, tylko fakt historyczny — takie partie wylosowały hasło
 * z listy składanej i muszą się tak dokończyć. Nie myl tej stałej z domyślną
 * dla nowej partii (`OGONKI_NOWEJ_PARTII`), bo są to dwie różne rzeczy, które
 * przypadkiem miały kiedyś tę samą wartość.
 */
export const DOMYSLNE_OGONKI = false;
/**
 * Pisownia **nowo zakładanej** partii: z ogonkami.
 *
 * Bez nich polski traci prawie wszystkie czasowniki — bezokoliczniki kończą
 * się na `-ć`, więc pięcioliterowych bez ogonków jest osiem wobec 352
 * z ogonkami, a partia „tylko czasowniki" nie da się założyć na żadnym progu.
 * `UMYSŁ` też nie jest hasłem, kiedy nie ma czym wpisać `Ł`.
 */
export const OGONKI_NOWEJ_PARTII = true;
/** Alfabet, w którym toczy się partia. Jedno miejsce, w którym stoi ten wybór. */
export const alfabetGry = (jezyk, ogonki) => ogonki ? ALFABETY_OGONKI[jezyk] : ALFABETY[jezyk];
/**
 * Postać słowa w regułach gry: **NFC i małe litery, nic więcej**.
 *
 * Nie ma tu ani składania, ani parametru języka — bo w regułach gry nie ma już
 * czego składać. NFC jest konieczne, bo źródła miejscami zapisują akcenty
 * rozłożone (`á` jako `a` + U+0301): bez złożenia to samo słowo miałoby dwa
 * zapisy bajtowe, `[...s].length` dawałoby dwójkę za jedną literę, a
 * wyszukiwanie binarne po liście nigdy by go nie znalazło. To jest ta sama
 * pułapka, co w `CLAUDE.md` — tylko że teraz dotyczy każdej partii, a nie
 * wyłącznie hiszpańskiej.
 */
export const postacGry = (slowo) => slowo.normalize("NFC").toLowerCase();
export const DLUGOSCI = [4, 5, 6];
export function jestDlugoscia(n) {
    return typeof n === "number" && DLUGOSCI.includes(n);
}
/**
 * Domyślna długość — **jedyna** stała, jaka po wariantach została z `DLUGOSC_HASLA`.
 *
 * Tamta stała siedziała wszędzie: w regexpach alfabetu, w walidacji strzału,
 * w warunku trafienia, w siatce i w trzech skryptach potoku. Dopóki tam stała,
 * wariant czteroliterowy nie był zmianą ustawienia, tylko przepisaniem gry —
 * więc zniknęła, a nie została „na wszelki wypadek": obok `dlugoscPartii(state)`
 * byłaby drugim źródłem tej samej liczby i pierwszy refaktor sięgnąłby po
 * krótszą nazwę.
 *
 * Ta wartość jest wyłącznie tym, co czyta partia zapisana **przed** dodaniem
 * pola: `settings.dlugosc ?? DOMYSLNA_DLUGOSC`. Kształtu stanu to nie zmienia,
 * więc `stateVersion` zostaje.
 */
export const DOMYSLNA_DLUGOSC = 5;
/**
 * „Słowo ma mieć **cztery litery**." / „**pięć liter**."
 *
 * Cała fraza, nie sam liczebnik: po polsku rzeczownik odmienia się razem z nim
 * i „cztery liter" jest błędem, który w komunikacie dla dwunastolatka widać
 * natychmiast. Sklejanie `${liczebnik} liter` w miejscu użycia dawałoby ten błąd
 * w każdym z trzech miejsc osobno.
 */
export const SLOWNIE_DLUGOSC = {
    4: "cztery litery",
    5: "pięć liter",
    6: "sześć liter",
};
/** To samo cyfrą — na przyciski, gdzie liczy się skanowanie wzrokiem. */
export const ETYKIETY_DLUGOSCI = {
    4: "4 litery",
    5: "5 liter",
    6: "6 liter",
};
/** Etykiety do ekranu nowej partii. Trzy przyciski, nie suwak. */
export const OPISY_DLUGOSCI = {
    4: "krótkie — więcej zgadywania niż dedukcji",
    5: "klasyczne",
    6: "długie — więcej liter, mniej kandydatów",
};
/**
 * `Ł` i `ł` wymagają **jawnego mapowania**, bo NFD ich nie rozkłada — to osobne
 * punkty kodowe, nie litery z akcentem. Ta sama pułapka, co w
 * `shared/src/normalize.ts`.
 */
const JAWNE = { "Ł": "L", "ł": "l" };
const JAWNE_ZNAKI = /[Łł]/g;
/**
 * `ñ`/`Ñ` chowamy w obszarze prywatnym (U+E000) **przed** NFD.
 *
 * NFD rozkłada `ñ` na `n` + U+0303, więc ścieżka „NFD i usuń znaki łączące",
 * która poprawnie obsługuje `á`, `é` i `ü`, zamieniłaby `caña` w `cana` — bez
 * błędu i bez ostrzeżenia. A `caña` i `cana` to dwa różne słowa: `ñ` ma
 * w hiszpańskim własne miejsce w alfabecie.
 *
 * To jest **lustrzane odbicie** problemu z `Ł`: `Ł` potrzebuje wyjątku, bo NFD
 * robi z nim za mało; `ñ` — bo robi za dużo. Oba komentarze muszą tu stać,
 * inaczej pierwszy refaktor „ujednolici" je w jedno i po cichu zepsuje jedno
 * z dwojga.
 *
 * Osłona działa we **wszystkich** językach, nie tylko w `es`. W polskim
 * i angielskim `ñ` nie należy do alfabetu, więc słowo z nią wypadnie na filtrze
 * alfabetu — a to jest zachowanie właściwe. Złożenie do `n` wpuściłoby
 * hiszpańskie zapożyczenie do polskiej puli jako inne, istniejące słowo.
 */
const STRAZNIK_N = "";
const ZNAKI_LACZACE = /\p{M}/gu;
/**
 * `ŻÓŁTY` → `zolty`, `caña` → `caña`, `ánimo` → `animo`.
 *
 * **Reguły gry tego nie wołają i wołać nie będą.** Zostaje wyłącznie dla
 * potoku danych i dla kuratora listy haseł:
 *
 *  - kolumna `zlozone` w `kandydaci.tsv` — po niej człowiek widzi, że `mila`
 *    i `miła` to para różniąca się jednym ogonkiem, i może zdecydować, czy chce
 *    w grze obie;
 *  - znacznik `podobne` w tym samym pliku, liczony z tej kolumny.
 *
 * Gdyby to wróciło do reguł, wróciłaby razem z tym ukryta równość `ł = l`:
 * gracz mógłby wpisać `jesli`, czyli ciąg, który nie jest polskim słowem,
 * a hasło `jeśli` pokazywałoby się na kafelkach jako `jesli`.
 *
 * Osłona `ñ` przed NFD zostaje **istotna**, bo potok czyta nią źródła: bez niej
 * `caña` po drodze staje się `cana`, czyli innym, istniejącym słowem.
 */
export function zloz(slowo, _jezyk) {
    const bezOgonkow = slowo
        .replace(/[ñÑ]/g, STRAZNIK_N)
        .replace(JAWNE_ZNAKI, (ch) => JAWNE[ch] ?? ch)
        .normalize("NFD")
        .replace(ZNAKI_LACZACE, "")
        .toLowerCase();
    // Wynik jest zawsze mały, więc `Ñ` wraca jako `ñ` — i tak ma być.
    return bezOgonkow.split(STRAZNIK_N).join("ñ");
}
/**
 * Wzorce budujemy raz, dla **każdej trójki (język, długość, ogonki)**.
 *
 * Osiemnaście regexpów zamiast dziewięciu, ale nadal zbudowanych z jednego
 * miejsca: kompilowanie `new RegExp` przy każdym strzale byłoby kosztem
 * w ścieżce, którą przechodzi każda próba każdego gracza.
 */
const WZORCE = new Map(JEZYKI.flatMap((jezyk) => DLUGOSCI.flatMap((dlugosc) => [false, true].map((ogonki) => [
    `${jezyk}:${dlugosc}:${ogonki}`,
    new RegExp(`^[${alfabetGry(jezyk, ogonki)}]{${dlugosc}}$`, "u"),
]))));
/**
 * Czy ciąg jest słowem o zadanej długości w alfabecie **tej partii**.
 *
 * To jest jedyne miejsce, w którym ustawienie `ogonki` cokolwiek rozstrzyga
 * o legalności: przy `false` `jeśli` odpada, bo `ś` nie należy do alfabetu, przy
 * `true` przechodzi. Kolorowanie i porównania nie wiedzą o tym parametrze nic.
 *
 * Domyślna długość jest tu po to, żeby wołający, których długość nie obchodzi
 * (testy alfabetu), nie musieli jej wypisywać — a nie po to, żeby dało się
 * o niej zapomnieć w regułach gry. Tam przychodzi z ustawienia, jak `ogonki`.
 */
export function pasujeDoAlfabetu(slowo, jezyk, dlugosc = DOMYSLNA_DLUGOSC, ogonki = DOMYSLNE_OGONKI) {
    return WZORCE.get(`${jezyk}:${dlugosc}:${ogonki}`)?.test(slowo) ?? false;
}
/**
 * Liczba liter, nie jednostek UTF-16.
 *
 * Wejście bywa w NFD (lemarios miejscami tak zapisuje akcenty): `á` jako
 * `a` + U+0301 ma wtedy `length === 2`, a jest jedną literą. Bez normalizacji
 * do NFC filtr „dokładnie pięć znaków" wycinałby losowo część hiszpańskich
 * haseł z akcentem.
 */
export function liczbaLiter(slowo) {
    return [...slowo.normalize("NFC")].length;
}
export const WSZYSTKIE_KLASY = ["subst", "verb", "adj", "adv", "inne"];
export function jestKlasa(x) {
    return WSZYSTKIE_KLASY.includes(x);
}
/** Nazwy do ekranu nowej partii i do nagłówka rundy. */
export const NAZWY_KLAS = {
    subst: "rzeczowniki",
    verb: "czasowniki",
    adj: "przymiotniki",
    adv: "przysłówki",
    inne: "inne",
};
/** Zawsze ta sama kolejność tagów w pliku i w widoku — inaczej diff pliku kłamie. */
export const kolejnoscKlas = (klasy) => {
    const zbior = new Set(klasy);
    return WSZYSTKIE_KLASY.filter((k) => zbior.has(k));
};
/* ————————————————————————— próg częstości ————————————————————————— */
/**
 * Progi rangi do ustawienia partii `maxRanga` — „jak popularne mają być hasła".
 *
 * Ranga to pozycja słowa na liście frekwencyjnej korpusu napisów (1 = najczęstsze).
 * Bez progu losowanie sięgało całej listy kandydatów, a to znaczy naprawdę
 * rzadkie słowa: w polskim ranga 100 000 to 76 wystąpień w całym korpusie,
 * ranga 200 000 — 23. `arras` (ranga 129 276) był legalnym hasłem.
 *
 * Osiem wartości z listy, nie dowolna liczba: to jest wybór „łatwiej /
 * trudniej", a nie parametr do strojenia. Hostowi nie wolno kazać zgadywać, czy
 * 137 000 różni się od 140 000 (nie różni się).
 *
 * **Dolne progi są do nauki języka.** Pięć i dziesięć tysięcy najczęstszych słów
 * to mniej więcej to, co dziecko może znać z lekcji angielskiego — partia
 * `en` na progu 5 000 jest ćwiczeniem słownictwa, a nie łamigłówką. Dla
 * polskiego te same progi są za ciasne i wypadną poniżej blokady w większości
 * kombinacji z częścią mowy; to jest oczekiwane, a nie usterka: ustawienie ma
 * pozwalać na jedno i na drugie.
 *
 * Stała stoi w `shared`, bo tę samą listę czytają trzy miejsca: skrypt budujący
 * `hasla.txt` (nagłówek z liczebnościami per próg), plugin (walidacja i filtr)
 * i ekran nowej partii (suwak).
 */
export const PROGI_RANGI = [
    5_000, 10_000, 25_000, 50_000, 75_000, 100_000, 200_000, 500_000,
];
/**
 * Domyślny próg. Sto tysięcy, bo to najszerszy próg, przy którym hasło padłe
 * w rundzie da się jeszcze obronić zdaniem „no przecież znasz to słowo".
 *
 * **Ta sama liczba stoi za każdym `?? DOMYSLNA_MAX_RANGA`** — partia zapisana
 * przed dodaniem tego pola ma się toczyć tak, jak gracze się umówili, a nie
 * zostać porzucona komunikatem o niezgodności wersji stanu.
 */
export const DOMYSLNA_MAX_RANGA = 100_000;
/**
 * Etykiety do ekranu nowej partii i do lobby — jedno miejsce na trzy ekrany.
 *
 * Dwa dolne progi mówią wprost, po co są: bez tego „5 000" wygląda na
 * najtrudniejsze ustawienie w grze, a jest najłatwiejszym.
 */
export const OPISY_RANGI = {
    5_000: "słówka z lekcji",
    10_000: "codzienne słowa",
    25_000: "dobrze znane",
    50_000: "znane",
    75_000: "znane i trochę rzadszych",
    100_000: "domyślnie — bez ekstremów",
    200_000: "także rzadkie",
    500_000: "wszystko, co mamy",
};
/**
 * Skala słowna rangi — do pokazania **po odsłonięciu** hasła.
 *
 * Po to jest: rodzina ma sobie kalibrować `maxRanga`. Po rundzie, w której padło
 * `arras`, widać „129 276 — bardzo rzadkie" i od razu wiadomo, że próg 200 tys.
 * jest za luźny. Sama liczba dwunastolatkowi nic nie mówi.
 *
 * Granice biorą się z rozkładu wystąpień w korpusie napisów (polski), a nie
 * z okrągłości:
 *
 * | ranga | wystąpień |
 * |---|---|
 * | 1 000 | 22 453 |
 * | 10 000 | 1 772 |
 * | 50 000 | 223 |
 * | 100 000 | 76 |
 * | 200 000 | 23 |
 *
 * Czyli: do 10 tys. słowo pada w korpusie tysiące razy (wszyscy je znają), do
 * 50 tys. setki (zna je dorosły), do 200 tys. kilkadziesiąt (dwunastolatek
 * mógł nigdy nie usłyszeć), wyżej — kilkanaście i mniej. Wszystkie trzy granice
 * są **także progami `maxRanga`** i to jest celowe: człowiek, który po rundzie
 * przeczyta „rzadkie", ma umieć przełożyć to na jedno kliknięcie w ekranie
 * nowej partii.
 */
const SKALA_RANGI = [
    [10_000, "bardzo popularne"],
    [50_000, "popularne"],
    [200_000, "rzadkie"],
    [Number.POSITIVE_INFINITY, "bardzo rzadkie"],
];
/**
 * `129276` → `„bardzo rzadkie, ranga 129 276"`; `null` → `null`.
 *
 * **Brak rangi zwraca `null`, a nie zero i nie „nieznane"** — hasło z pliku bez
 * trzeciej kolumny albo spoza listy częstości ma się pokazać samo, bez skali.
 * Zgadywanie rangi byłoby kłamstwem o trudności, a zero czytałoby się jako
 * „najpopularniejsze słowo świata".
 */
export function opisRangi(ranga) {
    if (typeof ranga !== "number" || !Number.isFinite(ranga) || ranga <= 0)
        return null;
    const nazwa = SKALA_RANGI.find(([gorna]) => ranga <= gorna)?.[1];
    if (nazwa === undefined)
        return null;
    return `${nazwa}, ranga ${ranga.toLocaleString("pl")}`;
}
/* ————————————————————————— klawiatura ————————————————————————— */
/**
 * Najwięcej klawiszy w rzędzie klawiatury ekranowej.
 *
 * Rachunek z dokumentu: przy szerokości 390 px, marginesach 6 px i odstępie
 * 3 px klawisz w rzędzie o `k` klawiszach ma `(378 − 3(k−1)) / k` px. Osiem
 * daje 44,6 px, dziewięć — 39,3 px, czyli poniżej progu, przez który odpadły
 * ogonki. Alfabet 26- i 27-literowy dostaje więc **czwarty rząd**, a nie
 * węższy klawisz.
 */
export const MAX_W_RZEDZIE = 8;
/**
 * Dzieli alfabet na możliwie małą liczbę rzędów tak, żeby żaden nie przekroczył
 * ośmiu klawiszy, a rzędy różniły się co najwyżej o jeden.
 *
 * 23 → 8/8/7, 26 → 7/7/6/6, 27 → 7/7/7/6. Trzy układy wpisane ręcznie to trzy
 * miejsca do poprawienia przy pierwszym czwartym języku — i trzy okazje, żeby
 * o jednym zapomnieć.
 */
export function podzielNaRzedy(litery) {
    if (litery.length === 0)
        return [];
    const rzedow = Math.ceil(litery.length / MAX_W_RZEDZIE);
    const podstawa = Math.floor(litery.length / rzedow);
    // Reszta rozkłada się po jednej literze na pierwsze rzędy — stąd 8/8/7,
    // a nie 7/8/8: szerszy rząd u góry, bo kciuk wraca do dołu.
    const dodatkowe = litery.length % rzedow;
    const rzedy = [];
    let i = 0;
    for (let r = 0; r < rzedow; r++) {
        const ile = podstawa + (r < dodatkowe ? 1 : 0);
        rzedy.push(litery.slice(i, i + ile));
        i += ile;
    }
    return rzedy;
}
/** Alfabet partii jako tablica liter — `ñ` jest jedną literą, nie dwiema. */
export const literyAlfabetu = (jezyk, ogonki = DOMYSLNE_OGONKI) => [
    ...alfabetGry(jezyk, ogonki),
];
/* ————————————————————————— klawisz „dodaj ogonek" ————————————————————————— */
/**
 * Cykl wariantów jednej litery: `l → ł → l`, `z → ź → ż → z`.
 *
 * ## Dlaczego klawisz modyfikujący, a nie 32 klawisze
 *
 * Rachunek szerokości jest ten sam, co przy kafelkach. Ekran 390 px, marginesy
 * po 6 px, odstęp 3 px; klawisz w rzędzie o `k` klawiszach ma `(378 − 3(k−1))/k`:
 *
 * | `k` | 6 | 7 | 8 | 9 |
 * |---|---|---|---|---|
 * | szerokość | 60,5 | 51,4 | **44,6** | 39,3 |
 *
 * Osiem w rzędzie to granica (44,6 px przyjęte, 39,3 px odrzucone), więc pełny
 * alfabet **mieści się w szerokości**: 32 → 8/8/8/8, 33 → 7/7/7/6/6. Problemem
 * jest **wysokość**. Rząd ma 56 px celu dotykowego plus 6 px odstępu, czyli
 * 62 px:
 *
 * | Układ | Rzędów liter | + rząd funkcyjny | Wysokość |
 * |---|---|---|---|
 * | pl bez ogonków (23) | 3 | 4 | 242 px |
 * | pl pełny (32) | 4 | 5 | 304 px |
 * | es pełny (33) | **5** | 6 | **366 px** |
 *
 * Siatka sześciu kafelków przy ośmiu próbach zajmuje `8×52 + 7×6 = 458 px`.
 * 458 + 366 + nagłówek + „Poddaję" to ponad 900 px na ekranie, który ma ich
 * 844 minus pasek adresu — czyli klawiatura schodzi poniżej krawędzi w partii,
 * którą host wolno może założyć. Dlatego pełnego alfabetu na klawiaturze nie ma.
 *
 * ## Dlaczego nie pozostałe dwa pomysły
 *
 * **Przełącznik układu** (klawisz zamieniający wszystkie litery na warianty
 * z ogonkiem) kosztuje tyle samo dotknięć, a dokłada tryb: w jadącym aucie
 * klawiatura, która zmienia się pod kciukiem, jest gorsza niż jedna litera do
 * poprawienia. **Długiego przytrzymania** nie ma z tego samego powodu, z którego
 * nie było go nigdy — w aucie nie działa.
 *
 * ## Jak działa ten
 *
 * Klawisz zamienia **ostatnią wpisaną literę** na jej kolejny wariant i wraca do
 * bazowej. `z` ma dwa warianty i to jest jedyny przypadek, w którym trzeba
 * dotknąć dwa razy — rozwiązuje go cykl, a nie druga reguła: `ż` po prostu stoi
 * w cyklu za `ź`. Klawisz **pokazuje wynik**, którym się stanie (po `l` widnieje
 * na nim `ł`), więc nie trzeba pamiętać, gdzie w cyklu się jest; bez litery
 * z wariantem jest wyłączony.
 *
 * Klawisz stoi w rzędzie funkcyjnym, między `⌫` a `ENTER`, więc **żaden rząd
 * literowy nie zmienia szerokości**: trzy klawisze na 378 px to 124 px każdy,
 * a `⌫` i `ENTER` zostają po przeciwnych stronach, żeby kciuk nie kasował
 * litery zamiast wysłać słowo.
 *
 * Angielski wariantów nie ma i klawisza nie dostaje — pusta mapa mówi to sama,
 * bez gałęzi „a jeśli angielski" w komponencie.
 */
export const WARIANTY_OGONKA = {
    pl: {
        a: "ą", ą: "a",
        c: "ć", ć: "c",
        e: "ę", ę: "e",
        l: "ł", ł: "l",
        n: "ń", ń: "n",
        o: "ó", ó: "o",
        s: "ś", ś: "s",
        // Jedyna litera z dwoma wariantami. Cykl, nie drugi klawisz: `ż` stoi za `ź`.
        z: "ź", ź: "ż", ż: "z",
    },
    en: {},
    es: {
        a: "á", á: "a",
        e: "é", é: "e",
        i: "í", í: "i",
        o: "ó", ó: "o",
        // `ü` jest w hiszpańskim wyłącznie po `g` (`pingüino`), więc siedzi za `ú`
        // w cyklu `u`, a nie na własnym klawiszu.
        u: "ú", ú: "ü", ü: "u",
        // `ñ` ma **także** własny klawisz (jest osobną literą alfabetu, nie akcentem).
        // Cykl obejmuje ją mimo to: kto pisze `n` i sięga po ogonek, chce `ñ`.
        n: "ñ", ñ: "n",
    },
};
/** Kolejny wariant litery albo `null`, gdy litera wariantów nie ma. */
export const nastepnyOgonek = (litera, jezyk) => WARIANTY_OGONKA[jezyk][litera] ?? null;
//# sourceMappingURL=litery.js.map